import { TaxReportSummary, TransactionStats } from '../components/tax-reports/types';
import { defaultCurrency, defaultLocaleFormatters } from '../constants/locale';
import { ChartValue } from './chart.types';
import { ApiResult } from './general.types';
import { RawChartValue, RawUserCoin, RawUserDetails } from './raw.types';
import { TaxReport, TaxReportDetailed, TaxReportWithYear } from './tax-reports.types';
import { CoinWithSymbol, Transaction, TransactionIssueType, TransactionType, TransactionTypeValue } from './transaction.types';
import { UserDetails } from './user.types';

// Convert string or number to number, return undefined if invalid
const toNumber = (value: string | number | undefined): number | undefined => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? undefined : num;
};

/**
 * Convert price from cents to euros
 * @param priceInCents Price in cents (as returned by the API)
 * @param fallbackPrice Optional fallback price in euros if priceInCents is undefined
 * @returns Price in euros
 */
export const convertCentsToEuros = (priceInCents: number | undefined, fallbackPrice?: number): number => {
    if (priceInCents === undefined || priceInCents === null || isNaN(priceInCents)) {
        return fallbackPrice ?? 0;
    }
    return priceInCents / 100;
};

/**
 * Format price in euros with currency symbol
 * @param priceInEuros Price in euros
 * @param currencySymbol Currency symbol to use, defaults to €
 * @returns Formatted price string with currency symbol
 */
export const formatPrice = (priceInEuros: number | undefined, currencySymbol: string = '€'): string => {
    if (priceInEuros === undefined || priceInEuros === null) {
        return `${currencySymbol}0.00`;
    }
    return `${currencySymbol}${priceInEuros.toFixed(2)}`;
};

export const transformChartValue = (data: RawChartValue[]): ChartValue[] => {
    return data.map((item) => ({
        ...item,
        value: toNumber(item.value) ?? 0,
    }));
};

export const transformUserDetails = (data: RawUserDetails): UserDetails => {
    return {
        ...data,
        ctId: toNumber(data.ctId) ?? 0,
    };
};

const getTransactionIssueTypes = (transaction: Transaction): TransactionIssueType[] => {
    const issues: TransactionIssueType[] = [];

    if (transaction.missingCostBasisAmount) {
        issues.push(TransactionIssueType.MissingCostBasis);
    }

    if (transaction.assetValueMissingType) {
        issues.push(TransactionIssueType.MissingPrice);
    }

    // Only return NoIssue if there are no other issues
    if (issues.length === 0) {
        issues.push(TransactionIssueType.NoIssue);
    }

    return issues;
};

export const transformTransaction = (data: ApiResult<Transaction[]>): ApiResult<Transaction[]> => {
    const res = data;
    res.data.forEach((transaction) => {
        transaction.buy = +transaction.buy;
        transaction.sell = +transaction.sell;
        transaction.fee = +transaction.fee;
        transaction.buyValueInFiat = +transaction.buyValueInFiat;
        transaction.sellValueInFiat = +transaction.sellValueInFiat;
        transaction.buyValueInBtc = +transaction.buyValueInBtc;
        transaction.sellValueInBtc = +transaction.sellValueInBtc;

        //Same field in the db
        transaction.contractAddress = transaction.buyPoolName;

        transaction.issueTypes = getTransactionIssueTypes(transaction);
        //get price per unit by using buy/sell amount and value in fiat
        if (transaction.buyValueInFiat > 0 && transaction.buy > 0) {
            transaction.buyPricePerUnit = +transaction.buy / +transaction.buyValueInFiat;
        } else {
            transaction.buyPricePerUnit = 0;
        }
        if (transaction.sellValueInFiat > 0 && transaction.sell > 0) {
            transaction.sellPricePerUnit = +transaction.sell / +transaction.sellValueInFiat;
        } else {
            transaction.sellPricePerUnit = 0;
        }
    });
    return res;
};

export const transformUserCoins = (coins: RawUserCoin[]): CoinWithSymbol[] => {
    return coins.map((coin) => ({
        name: coin.name || '',
        ticker: coin.ticker || '',
        active: Boolean(coin.visible || true),
        symbol: coin.ticker || '',
    }));
};

export const sortTaxReportsByYear = (reports: TaxReportWithYear[]): TaxReportWithYear[] => {
    return [...reports].sort((a, b) => b.tax_year.localeCompare(a.tax_year));
};

export const transformTaxReport = (reports: TaxReport[]): TaxReportWithYear[] => {
    return reports.map((report) => {
        let tax_year = report.tax_end_date.substring(0, 4);
        if (
            tax_year !== report.parameters.start_date.substring(0, 4) ||
            tax_year !== report.parameters.end_date.substring(0, 4)
        ) {
            console.log(
                'Warning: Tax year does not match start and end date: ',
                tax_year,
                report.parameters.start_date,
                report.parameters.end_date
            );
        }
        return {
            ...report,
            tax_year,
        };
    });
};

export const transformToTaxReportSummary = (report: TaxReportDetailed): TaxReportSummary => {
    const marginSales = report.sales.filter((sale) => sale.pool.transaction_type === 7); // Assuming 7 is margin transaction type
    const marginGains = marginSales.reduce((sum, sale) => sum + (sale.gain > 0 ? sale.gain : 0), 0);
    const marginLosses = Math.abs(marginSales.reduce((sum, sale) => sum + (sale.gain < 0 ? sale.gain : 0), 0));

    const privateDisposalSales = report.sales.filter((sale) => sale.pool.transaction_type !== 7);
    const privateDisposalsGains = privateDisposalSales.reduce((sum, sale) => sum + (sale.gain > 0 ? sale.gain : 0), 0);
    const nonTaxablePrivateDisposals = privateDisposalSales
        .filter((sale) => !sale.short_term)
        .reduce((sum, sale) => sum + Math.abs(sale.gain), 0);

    return {
        taxableIncome: {
            privateDisposals: privateDisposalsGains,
            capitalGains: {
                marginGains,
                marginLosses,
            },
            otherIncome: 0, // This might need to come from a different endpoint
        },
        nonTaxableIncome: {
            privateDisposals: nonTaxablePrivateDisposals,
            otherIncome: 0, // This might need to come from a different endpoint
        },
        otherTransactions: {
            receivedDonations: 0, // These values might need to come from a different endpoint
            sentDonations: 0,
            lostCoins: 0,
            mining: 0,
            minting: 0,
        },
    };
};

export const transformToTransactionStats = (report: TaxReportDetailed): TransactionStats => {
    const uniqueIncomingTypes = new Set(
        report.sales.filter((sale) => sale.gain > 0).map((sale) => sale.pool.transaction_type.toString())
    );

    const uniqueOutgoingTypes = new Set(
        report.sales.filter((sale) => sale.gain < 0).map((sale) => sale.pool.transaction_type.toString())
    );

    return {
        incomingTypes: {
            count: uniqueIncomingTypes.size,
            types: Array.from(uniqueIncomingTypes),
        },
        outgoingTypes: {
            count: uniqueOutgoingTypes.size,
            types: Array.from(uniqueOutgoingTypes),
        },
    };
};

export const convertToReactCountryCode = (userDetails: UserDetails) => {
    return {
        ...userDetails,
        currency: userDetails.currency || defaultCurrency,
        locale: userDetails.locale?.replace('_', '-') || defaultLocaleFormatters,
    };
};

export const removeUnsupportedTypes = (types: TransactionType[]): TransactionType[] => {
    return types.filter((type) => type.value !== TransactionTypeValue.MarginTrade && type.value !== TransactionTypeValue.DerivativesFutureTrade);
};